import Cookies from 'js-cookie';

export const SESSION_COOKIE_NAME = 'alpha_air_booking_session';
export const CUSTOMER_DATA_COOKIE_NAME = 'eclipse_customer_data';
export const BOOKING_DATA_COOKIE_NAME = 'eclipse_booking_data';

export interface SessionData {
    sessionId: string;
    leadId?: string;
    customerData?: any;
    formData?: any; // Form data separate from processed customer data
    bookingData?: any;
    currentStep?: string;
    userJourney?: any;
    bookingId?: string; // Database booking ID for Trak integration
    /** Identity of the booking attempt that produced `bookingId`. Format:
     *  `${customerId}|${date}|${startTime}`. Used by ConfirmationPage to verify
     *  that the persisted bookingId actually corresponds to the CURRENT slot
     *  before short-circuiting. Without this key a returning customer's old
     *  bookingId would suppress legitimate new bookings. */
    bookingContextKey?: string;
    addressCoordinates?: { lat: number; lng: number }; // Google Places coordinates
    // Address validation state (backwards compatible)
    addressValidation?: {
        isValidated: boolean;
        placeId?: string;
        coordinates?: { lat: number; lng: number };
        timestamp?: string; // When validation occurred
    };
    simproQuoteData?: {
        customerId?: string;
        siteId?: string;
        quoteId?: string;
        sectionId?: string;
        costCenterId?: string;
        scheduleId?: string;
        jobId?: string;
        scheduledDate?: string;
        scheduledTime?: string;
    };
    utmData?: {
        source?: string;
        medium?: string;
        campaign?: string;
        content?: string;
        term?: string;
    };
    warrantySubmission?: {
        reference: string;
        customerEmail: string;
        productMake: string;
        productModel: string;
        submittedAt?: string;
    };
    /** ISO timestamp of when this session was last persisted. Used to expire stale
     *  data so a returning customer doesn't carry forward a previous bookingId /
     *  simproData (which causes ConfirmationPage to fake a confirmation). */
    _savedAt?: string;
}

/** How long a stored session is considered valid. After this, booking-creation
 *  state (bookingId / simproData) is dropped on read so the next flow starts fresh. */
export const SESSION_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

export const generateSessionId = (): string => {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// Backup key for critical customer data
const CUSTOMER_BACKUP_KEY = 'alpha_customer_backup';

export const getSessionData = (): SessionData | null => {
    try {
        // First try localStorage (larger capacity)
        const sessionData = localStorage.getItem(SESSION_COOKIE_NAME);
        if (sessionData) {
            const parsed = JSON.parse(sessionData);

            // TTL ENFORCEMENT: If the session is older than SESSION_TTL_MS, drop
            // any booking-creation state that would otherwise short-circuit a new
            // booking flow (bookingId / simproData / simproQuoteData). We keep
            // customerData/formData around for prefill convenience.
            if (parsed._savedAt) {
                const ageMs = Date.now() - new Date(parsed._savedAt).getTime();
                if (Number.isFinite(ageMs) && ageMs > SESSION_TTL_MS) {
                    if (parsed.bookingId || parsed.simproQuoteData || parsed.bookingData?.simproData) {
                        console.log('⏰ Session older than TTL - clearing stale booking state', {
                            ageHours: (ageMs / 3600_000).toFixed(1),
                            hadBookingId: !!parsed.bookingId,
                        });
                        delete parsed.bookingId;
                        delete parsed.bookingContextKey;
                        delete parsed.simproQuoteData;
                        if (parsed.bookingData) delete parsed.bookingData.simproData;
                        localStorage.setItem(SESSION_COOKIE_NAME, JSON.stringify(parsed));
                    }
                }
            }

            // IRON-CLAD: If customerData is missing, try to restore from backup
            if (!parsed.customerData || !parsed.customerData.firstName) {
                const backupData = localStorage.getItem(CUSTOMER_BACKUP_KEY);
                if (backupData) {
                    try {
                        const backup = JSON.parse(backupData);
                        if (backup.firstName) {
                            console.log("🔄 RECOVERY: Restoring customerData from backup");
                            parsed.customerData = backup;
                            // Re-save the restored data
                            localStorage.setItem(SESSION_COOKIE_NAME, JSON.stringify(parsed));
                        }
                    } catch (e) {
                        console.warn("Failed to parse customer backup:", e);
                    }
                }
            }

            return parsed;
        }

        // Fallback to cookies for backwards compatibility
        const sessionCookie = Cookies.get(SESSION_COOKIE_NAME);
        if (sessionCookie) {
            const data = JSON.parse(sessionCookie);
            // Migrate from cookie to localStorage
            localStorage.setItem(SESSION_COOKIE_NAME, sessionCookie);
            Cookies.remove(SESSION_COOKIE_NAME);
            return data;
        }

        return null;
    } catch (error) {
        console.error('Error parsing session data:', error);
        return null;
    }
};

export const setSessionData = (data: SessionData): void => {
    try {
        // Stamp every write so the TTL check in getSessionData has a reference point.
        data = { ...data, _savedAt: new Date().toISOString() };
        const jsonString = JSON.stringify(data);
        const sizeInBytes = new Blob([jsonString]).size;

        console.log("🔍 DEBUG: Setting session data:", {
            dataSize: sizeInBytes,
            storageMethod: 'localStorage',
            selectedTimeSlot: data.bookingData?.selectedTimeSlot,
            jsonStringLength: jsonString.length,
        });

        // Use localStorage instead of cookies to avoid size limitations
        localStorage.setItem(SESSION_COOKIE_NAME, jsonString);

        // IRON-CLAD: Backup customerData separately for recovery
        if (data.customerData && data.customerData.firstName) {
            localStorage.setItem(CUSTOMER_BACKUP_KEY, JSON.stringify(data.customerData));
        }

        // Verify the data was actually set
        const readBack = localStorage.getItem(SESSION_COOKIE_NAME);
        if (readBack) {
            const readBackSize = new Blob([readBack]).size;
            console.log("🔍 DEBUG: localStorage verification:", {
                wasSet: true,
                originalSize: sizeInBytes,
                readBackSize: readBackSize,
                dataIntact: sizeInBytes === readBackSize,
            });

            try {
                const parsedBack = JSON.parse(readBack);
                console.log("🔍 DEBUG: Parsed verification:", {
                    selectedTimeSlotStillThere: !!parsedBack.bookingData?.selectedTimeSlot,
                    bookingDataExists: !!parsedBack.bookingData,
                    customerDataExists: !!parsedBack.customerData,
                });
            } catch (parseError) {
                console.error("❌ Failed to parse localStorage data:", parseError);
            }
        } else {
            console.error("❌ localStorage was not set!");
        }
    } catch (error) {
        console.error('Error setting session data:', error);
    }
};

export const updateSessionData = (updates: Partial<SessionData>): void => {
    const currentData = getSessionData() || { sessionId: generateSessionId() };

    console.log("🔍 DEBUG: updateSessionData called with:", {
        currentData,
        updates,
        hasBookingDataUpdate: !!updates.bookingData,
        hasCurrentBookingData: !!currentData.bookingData,
        hasCustomerDataUpdate: !!updates.customerData,
        hasCurrentCustomerData: !!currentData.customerData,
    });

    // Start with current data
    let updatedData = { ...currentData };

    // Apply updates, but handle nested objects carefully
    for (const key of Object.keys(updates) as (keyof SessionData)[]) {
        const updateValue = updates[key];

        // Skip undefined values - never overwrite with undefined
        if (updateValue === undefined) {
            continue;
        }

        // Handle nested object merging for critical data
        if (key === 'bookingData' && currentData.bookingData && updateValue) {
            updatedData.bookingData = {
                ...currentData.bookingData,
                ...updateValue,
            };
        } else if (key === 'customerData' && currentData.customerData && updateValue) {
            // IRON-CLAD: Always merge customerData, never replace entirely
            updatedData.customerData = {
                ...currentData.customerData,
                ...updateValue,
            };
        } else if (key === 'formData' && currentData.formData && updateValue) {
            // Also protect formData
            updatedData.formData = {
                ...currentData.formData,
                ...updateValue,
            };
        } else {
            (updatedData as any)[key] = updateValue;
        }
    }

    // SAFETY CHECK: Ensure critical data wasn't lost
    if (currentData.customerData && !updatedData.customerData) {
        console.warn("⚠️ SAFETY: customerData would have been lost, restoring it");
        updatedData.customerData = currentData.customerData;
    }
    if (currentData.bookingData && !updatedData.bookingData) {
        console.warn("⚠️ SAFETY: bookingData would have been lost, restoring it");
        updatedData.bookingData = currentData.bookingData;
    }

    console.log("🔍 DEBUG: Final data being saved:", updatedData);
    setSessionData(updatedData);
};

// Preserve form data when updating session
export const updateSessionDataPreservingForm = (updates: Partial<SessionData>): void => {
    const currentData = getSessionData() || { sessionId: generateSessionId() };

    // Always preserve existing formData unless explicitly overridden
    const updatedData = {
        ...currentData,
        ...updates,
        // Preserve formData if it exists and wasn't explicitly updated
        formData: updates.formData || currentData.formData
    };

    setSessionData(updatedData);
};

export const clearSessionData = (): void => {
    // Clear from both localStorage and cookies
    localStorage.removeItem(SESSION_COOKIE_NAME);
    localStorage.removeItem(CUSTOMER_BACKUP_KEY); // Also clear the backup
    Cookies.remove(SESSION_COOKIE_NAME);
    Cookies.remove(CUSTOMER_DATA_COOKIE_NAME);
    Cookies.remove(BOOKING_DATA_COOKIE_NAME);
};

/**
 * Builds the booking context key used to verify that a persisted `bookingId`
 * actually corresponds to the CURRENT slot the user is trying to confirm.
 * Returns null if any input is missing.
 */
export const buildBookingContextKey = (
    customerId: string | number | undefined | null,
    date: string | undefined | null,
    startTime: string | undefined | null,
): string | null => {
    if (!customerId || !date || !startTime) return null;
    return `${customerId}|${date}|${startTime}`;
};

/**
 * Clears stale booking-creation state from a previous flow (bookingId,
 * persisted simproData/simproQuoteData) WITHOUT wiping the rest of the
 * session (customerData, formData, utmData, etc.).
 *
 * Why this exists:
 *   localStorage has no TTL, so a returning customer carries the previous
 *   booking's bookingId + simproData forward. The ConfirmationPage's
 *   "duplicate prevention" check (`if (sessionData.bookingId) return;`)
 *   then short-circuits and shows a fake "Booking Confirmed" page with the
 *   OLD SimPro reference and no new DB row / SimPro job / SMS being created.
 *
 * Call this at the start of a new booking flow (e.g. after a successful
 * contact-form submission) to guarantee a clean slate.
 */
export const clearStaleBookingState = (): void => {
    try {
        const raw = localStorage.getItem(SESSION_COOKIE_NAME);
        if (!raw) return;
        const parsed = JSON.parse(raw);

        delete parsed.bookingId;
        delete parsed.bookingContextKey;
        if (parsed.bookingData) {
            delete parsed.bookingData.simproData;
        }
        delete parsed.simproQuoteData;

        localStorage.setItem(SESSION_COOKIE_NAME, JSON.stringify(parsed));
        console.log('🧹 Cleared stale booking state (bookingId / simproData) from previous flow');
    } catch (error) {
        console.warn('clearStaleBookingState failed:', error);
    }
};

export const captureUTMParameters = () => {
    const urlParams = new URLSearchParams(window.location.search);
    // Also read hash fragment as campaign code (e.g. #WINTERWARMTH)
    const hashCode = window.location.hash.replace(/^#/, '').trim().toLowerCase() || undefined;
    return {
        source: urlParams.get('utm_source') || undefined,
        medium: urlParams.get('utm_medium') || undefined,
        campaign: urlParams.get('utm_campaign') || undefined,
        content: urlParams.get('utm_content') || hashCode,
        term: urlParams.get('utm_term') || undefined,
    };
};

export const initializeSession = (): string => {
    let sessionData = getSessionData();

    if (!sessionData || !sessionData.sessionId) {
        const sessionId = generateSessionId();

        // Capture UTM parameters from URL
        const utmData = captureUTMParameters();

        sessionData = {
            sessionId,
            utmData,
            currentStep: 'contact'
        };

        setSessionData(sessionData);
    }

    return sessionData.sessionId;
};
